import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Dropdown, DropdownItem, DropdownList, Input } from '@cokitchen/cokitchen-components';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { CoKitchenPolygons } from 'models/cokitchen.model';
import { ALL_POLYGONS_ID } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';

type Props = {
  polygonId: string;
  setPolygonId: (id: string) => void;
  hasAll?: boolean;
};

const PolygonSelector: FC<Props> = ({ polygonId, setPolygonId, hasAll }) => {
  const didMount = useRef(false);
  const { activeCokitchen } = useDashboardContext();
  const [allPolygons, setAllPolygons] = useState([] as CoKitchenPolygons[]);
  const [finalList, setFinalList] = useState([] as CoKitchenPolygons[]);
  const [filteredPolygons, setFilteredPolygons] = useState([] as CoKitchenPolygons[]);
  const [selectedPolygon, setSelectedPolygon] = useState({} as CoKitchenPolygons);
  const [openDropdown, setOpenDropdown] = useState(false);

  const loadPolygons = async () => {
    const { cokitchen_polygons } = activeCokitchen ?? {};
    const sortedList = cokitchen_polygons?.sort((a, b) => b?.name.localeCompare(a?.name)) ?? [];
    let list = [...sortedList];
    if (hasAll) {
      const allItem = { name: 'All', id: ALL_POLYGONS_ID } as CoKitchenPolygons;
      list = [allItem, ...sortedList];
    }
    setFinalList(list);
    setAllPolygons(sortedList);
    setFilteredPolygons(sortedList);
  };

  useEffect(() => {
    loadPolygons();
  }, [activeCokitchen]);

  useEffect(() => {
    if (didMount.current && finalList?.length > 0) {
      setSelectedPolygon(finalList[0]);
    }
  }, [finalList]);

  useEffect(() => {
    if (didMount.current && selectedPolygon.id !== polygonId) {
      setPolygonId(selectedPolygon.id ?? ALL_POLYGONS_ID);
    }
    setOpenDropdown(false);
    didMount.current = true;
  }, [selectedPolygon]);

  useEffect(() => {
    setOpenDropdown(false);
  }, [polygonId]);

  useEffect(() => {
    if (!openDropdown) setFilteredPolygons(allPolygons);
  }, [openDropdown]);

  const filterSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    const filtered = allPolygons.filter(({ name }) => name.toLowerCase().includes(searchValue));
    setFilteredPolygons(filtered);
  };

  return (
    <Dropdown
      visible={openDropdown}
      onClickOutside={() => setOpenDropdown(false)}
      content={
        <div
          onClick={() => setOpenDropdown(!openDropdown)}
          className='px-3 py-[7.5px] inline-flex items-center text-sm font-medium rounded-[20px] bg-red-1 cursor-pointer'>
          {selectedPolygon.id === ALL_POLYGONS_ID ? 'All polygons' : selectedPolygon.name}
          <ChevronDownIcon />
        </div>
      }>
      <DropdownList className='min-w-[300px]'>
        <div className='px-2 pb-1 mb-1 border-b border-b-[#ebebeb] border-dashed'>
          <Input
            type='search'
            inputClass='py-2 text-sm'
            placeholder='Search polygon'
            small
            onChange={filterSearch}
          />
        </div>
        <ul className='max-h-[300px] overflow-auto'>
          {hasAll && (
            <DropdownItem
              onClick={() => setSelectedPolygon({ id: ALL_POLYGONS_ID } as CoKitchenPolygons)}>
              <Input
                type='radio'
                className='text-sm'
                checked={!polygonId}
                readOnly
                label='All Polygons'
              />
            </DropdownItem>
          )}
          {filteredPolygons.length === 0 ? (
            <DropdownItem isLabel className='py-4'>
              There are no polygons with search input
            </DropdownItem>
          ) : (
            filteredPolygons.map((p, i) => (
              <DropdownItem key={i} onClick={() => setSelectedPolygon(p)}>
                <Input
                  className='text-sm'
                  type='radio'
                  checked={polygonId === p.id}
                  readOnly
                  label={p.name}
                />
              </DropdownItem>
            ))
          )}
        </ul>
      </DropdownList>
    </Dropdown>
  );
};

export default PolygonSelector;
